import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaMountain, FaSnowflake
} from 'react-icons/fa';
import './ShimlaManaliEscape.css';

const ShimlaManaliEscape = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival at Chandigarh & Transfer to Shimla",
      points: [
        "Pickup from Chandigarh Railway Station / Airport where our representative will receive you.",
        "Proceed towards Shimla, the former summer capital of British India.",
        "Enjoy the beautiful drive through the mountains with scenic Himalayan views.",
        "Upon arrival: Hotel check-in, Evening free to relax or explore Mall Road.",
        "Overnight stay in Shimla. Approx. Distance: 115 km | Time: 4 hours"
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Shimla & Kufri Sightseeing",
      points: [
        "After breakfast, proceed for sightseeing around Shimla.",
        "Visit Kufri: Popular hill station known for snow activities and panoramic views.",
        "Explore Mall Road, The Ridge, and the historic Christ Church.",
        "Evening free to explore Shimla’s local markets.",
        "Overnight stay in Shimla."
      ],
      icon: <FaSnowflake />
    },
    {
      day: 3,
      title: "Shimla to Manali via Kullu & Manikaran",
      points: [
        "After breakfast, check out and proceed towards Manali.",
        "Enjoy a scenic drive along the Beas River through the beautiful Kullu Valley.",
        "Visit Manikaran Sahib Gurudwara, famous for its natural hot water springs.",
        "En route enjoy views of Kullu Valley (River rafting optional).",
        "Overnight stay in Manali. Approx. Distance: 250 km | Time: 7–8 hours"
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 4,
      title: "Manali Local Sightseeing",
      points: [
        "After breakfast, explore the local gems of Manali.",
        "Visit Hadimba Devi Temple: An ancient wooden temple in cedar forests.",
        "Explore Old Manali: Known for its unique cafés and local culture.",
        "Visit Tibetan Monastery and stroll through the lively Mall Road.",
        "Overnight stay in Manali."
      ],
      icon: <FaMountain />
    },
    {
      day: 5,
      title: "Solang Valley, Atal Tunnel & Sissu Village Excursion",
      points: [
        "Proceed for a full-day excursion to Solang Valley (Adventure sports hub).",
        "Drive through the iconic Atal Tunnel, one of the world's longest high-altitude tunnels.",
        "Visit Sissu Village in Lahaul Valley, famous for its waterfalls and landscapes.",
        "Experience paragliding or skiing in Solang (at extra cost).",
        "Overnight stay in Manali."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 6,
      title: "Departure from Manali to Delhi",
      points: [
        "Enjoy your final Himalayan breakfast and complete check-out formalities.",
        "Proceed towards Delhi for your onward journey.",
        "The tour concludes with beautiful memories of the mountains.",
        "Approx. Distance: 520 km | Estimated Travel Time: 10–12 hours"
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Shimla & Manali Himalayan Escape – <br /> Shimla, Kullu & Manali</h1>
          <p className="trip-duration">Duration: 5 Nights & 6 Days of Adventure</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Shimla, Kullu, Manali, Lahaul</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Private & Personalized</p></div></div>
          <div className="info-row type-tag"><FaMountain className="header-icon" /><p>Himalayan Getaway</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?q=80&w=1200" alt="Shimla" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://images.unsplash.com/photo-1609920658906-8223bd289001?q=80&w=301&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Manali" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1621340978972-1e3717f1eef6?q=80&w=869&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Kullu" />
          </div>
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1713063968789-adf139c4a1eb?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Solang Valley" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Detailed Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy & Inclusions</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Tour Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore the colonial charm and scenic beauty of Shimla</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit Kufri for snow views and Himalayan panoramas</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Scenic drive through the beautiful Kullu Valley</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the sacred Manikaran Sahib with natural hot springs</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Paragliding and adventure activities in Solang Valley</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Drive through the iconic Atal Tunnel</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit picturesque Sissu Village in Lahaul Valley</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore Hadimba Devi Temple and Old Manali culture</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in comfortable hotels (2N Shimla | 3N Manali)</li>
              <li><FaClock /> 5 Nights / 6 Days stay as per itinerary</li>
              <li><FaStar /> Accommodation on double / triple sharing basis</li>
              <li><FaUtensils /> Meal Plan: MAP (5 Breakfasts & 5 Dinners at hotels)</li>
              <li><FaMapMarkerAlt /> All sightseeing and transfers in a private vehicle</li>
              <li><FaBus /> Pickup from Chandigarh and drop to Delhi</li>
              <li><FaCheckCircle /> Driver charges, fuel, toll taxes, parking, and permits</li>
              <li><FaShieldAlt /> Tour coordination and assistance throughout the trip</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets for arrival and departure</li>
              <li><FaTimesCircle /> Entry fees for sightseeing, monuments, or parks</li>
              <li><FaTimesCircle /> Adventure activities (Rafting, Paragliding, Skiing, etc.)</li>
              <li><FaTimesCircle /> Personal expenses (Tips, laundry, shopping, etc.)</li>
              <li><FaUtensils /> Lunch or any meals not mentioned in inclusions</li>
              <li><FaShieldAlt /> Travel insurance or medical expenses</li>
              <li><FaTimesCircle /> Expenses due to natural calamities (landslides, etc.)</li>
              <li><FaBus /> Extra usage of vehicle beyond the itinerary</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">About This Journey</h2>
          <p className="about-text">
            The Shimla & Manali Himalayan Escape is a scenic journey through the beautiful hill stations of Himachal Pradesh. This tour takes travelers through the charming towns of Shimla and Manali, offering a perfect combination of natural beauty, adventure, cultural experiences, and mountain landscapes.
            <br /><br />
            From the colonial charm of Shimla and the snowy views of Kufri to the spiritual atmosphere of Manikaran Sahib and the adventure-filled Solang Valley, this tour allows visitors to experience some of the most popular destinations in the Indian Himalayas. It is ideal for those looking for a relaxing yet adventurous getaway.
          </p>
        </motion.section>

        {/* TERMS & PRIVACY */}
        <div className="accordion-item">
          <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Terms & Conditions</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
          </Link>
        </div>

        <div className="accordion-item">
          <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <span>Privacy Policy</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
          </Link>
        </div>

        {/* TRAVEL INFO */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{ width: '100%' }}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Our team will assist you in planning convenient travel arrangements for a smooth journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default ShimlaManaliEscape;